<template>
    <div v-if="loading">
        <Loader/>
    </div>
    <div class="status-content" v-else>

        <ContractPreview :url="pdfPreviewUrl" v-show="showPdfViewver"/>

        <Header :title="title" :subtitle="subtitle" :link="link">
            <div slot="actions" class="actions">
                <!-- Recall contract -->
                <button v-if="!this.draftContract.is_completed" v-on:click="revoke()" class="transparent">
                    {{ $t('button.contract.recall') }}
                </button>
                <!-- Show contract -->
                <button :disabled="generatingPdf" v-on:click="preview()" class="transparent">
                    <template v-if="!generatingPdf">{{ $t('digital.tabs.review.preview') }}</template>
                    <template v-if="generatingPdf">{{ $t('digital.tabs.review.generatingpdf') }} ...</template>
                </button>
                <!-- edit contract -->
                <button v-if="!this.draftContract.is_completed" v-on:click="edit()" class="transparent">
                    {{ $t('button.contract.edit') }}
                </button>
                <a class="circle-icon">
                    <table-dropdown>
                        <li v-on:click="downloadModal()">{{ $t('button.download.label') }}</li>
                        <li v-on:click="deleteContract()">{{ $t('draft-contracts.delete') }}</li>
                    </table-dropdown>
                </a>
            </div>
        </Header>

        <div class="inner main-content">

            <Grid :fields="gridFields"/>

            <!-- Visual status indicator -->
            <div class="status-row four-items" :class="{'three-items' : draftContract.requires_landlord_approval}">

                <!-- Contract -->
                <StatusCol class="finished">
					<span slot="icon">
						<SvgIcon icon="contract-icon"/>
					</span>
                    <span slot="label">{{ $t('general.label.contract') }}</span>
                    <div class="status-circle" slot="status">
                        <div class="icon">
                            <SvgIcon icon="white-checkmark"/>
                        </div>
                    </div>
                </StatusCol>


                <!-- Approval -->
                <StatusCol v-if="draftContract.requires_landlord_approval"
                           v-bind:class="{ progress: approvalStatusPercent !== 100, finished: approvalStatusPercent === 100 }">
                    <span slot="inner-status" v-bind:style="{ width: approvalStatusPercent + '%' }"></span>
                    <span slot="icon">
						<SvgIcon icon="watch-icon"/>
					</span>
                    <span slot="label">{{ $t('digital.tabs.review.approval-status') }}</span>
                    <div class="status-circle" slot="status">
                        <div class="icon" v-if="approvalStatusPercent !== 100">
                            <SvgIcon icon="loading"/>
                        </div>
                        <div class="icon" v-else>
                            <SvgIcon icon="white-checkmark"/>
                        </div>
                    </div>
                    <div class="list" slot=list>
                        <ContractStatusItemList :list="approvalStatusList"/>
                        <button class="purple" v-on:click="sendEmail()" v-if="showSendEmail">
                            {{ $t('digital.tabs.sendemail') }}
                        </button>
                    </div>
                </StatusCol>


                <!-- Signing -->
                <StatusCol
                    v-bind:class="{ progress: signingStatusPercent !== 100, finished: signingStatusPercent === 100 }"
                >
                    <span slot="inner-status" v-bind:style="{ width: signingStatusPercent + '%' }"></span>
                    <span slot="icon">
						<SvgIcon icon="pen-icon"/>
					</span>
                    <span slot="label">{{ $t('general.label.signing') }}</span>
                    <div class="status-circle" slot="status">
                        <div class="icon" v-if="signingStatusPercent !== 100 ">
                            <SvgIcon icon="loading"/>
                        </div>
                        <div class="icon" v-else>
                            <SvgIcon icon="white-checkmark"/>
                        </div>
                    </div>
                    <div class="list" slot=list>
                        <ContractStatusItemList :list="signingStatusList"/>
                    </div>
                </StatusCol>

                <!-- Finished -->
                <StatusCol v-bind:class="{ finished: signingStatusPercent === 100 }">
					<span slot="icon">
						<SvgIcon icon="check-icon"/>
					</span>
                    <span slot="label">{{ $t('general.label.finished') }}</span>
                    <div class="status-circle" slot="status">
                        <div class="icon">
                            <SvgIcon icon="white-checkmark"/>
                        </div>
                    </div>
                </StatusCol>

            </div>
        </div>

        <!-- Modal -->
        <download-contract-modal v-if="showDownloadModal"
                                 v-on:close="showDownloadModal = false"></download-contract-modal>
    </div>
</template>

<script>
import Vue from 'vue';
import SvgIcon from '../../components/Svg.vue';

import Loader from '../../components/Loader.vue';
import Header from '../../components/Header.vue';
import Grid from '../../components/Grid.vue';
import StatusCol from '../../components/StatusCol.vue';
import TableDropdown from '../../components/TableDropdown';
import ContractPreview from '../../components/ContractPreview';

import ContractStatusItemList from '../../components/ContractStatusItemList.vue';
import {mapGetters} from 'vuex';

import DownloadContractModal from "../../components/DownloadContractModal";

Vue.filter('formatDate', function (value) {
    if (value) {
        return moment(String(value)).format('DD-MM-YYYY')
    }
});

export default {
    data: () => ({
        loading: true,
        title: '',
        subtitle: '',
        link: '',
        draftContract: {
            tenants: [],
            definition: {}
        },
        landlordName: '',
        inProgress: false,
        generatingPdf: false,
        showPdfViewver: false,
        pdfPreviewUrl: '',
        generatingDownloadPdf: false,
        downloadUrlLink: '',
        showDownloadModal: false,
        termsAccepted: false,
    }),
    computed: {
        ...mapGetters([
            'getToken'
        ]),
        showSendEmail() {
            return _(this.draftContract.tenants).filter((tenant) => tenant.pivot.is_sent === true).size() === 0 && this.draftContract.landlord_approved_at !== null;
        },
        tenantName() {

            let name = this.draftContract.tenants[0].name || this.draftContract.tenants[0].email;

            if (this.draftContract.tenants.length > 1) {
                name += ' + ' + (this.draftContract.tenants.length - 1);
            }

            return name;
        },
        gridFields() {
            return [
                {
                    title: this.$t('contract-status.current-status.label'),
                    value: this.currentStatus
                },
                {
                    title: this.$t('contract-status.tenant.label'),
                    value: this.tenantName
                },
                {
                    title: this.$t('contract-status.landlord.label'),
                    value: this.landlordName
                },
                {
                    title: this.$t('contract-status.occupancy-date.label'),
                    value: this.dateFormat(this.draftContract.definition.occupancy_date)
                }
            ];
        },
        currentStatus() {
            var label = 'contract-status.pending';
            // var check = [
            //     this.statusListsApproval,
            //     this.statusListsSigning
            // ];
            // for (var i in check) {
            //     for (var j in check[i]) {
            //         if (check[i][j].checked) {
            //             label = check[i][j].label;
            //         }
            //     }
            // }
            if (this.draftContract.is_completed) {
                label = 'contract-status.finished'
            }
            return this.$t(label);
        },

        signingStatusPercent() {
            let checked = _(this.signingStatusList).filter((status) => status.checked === true).size();
            let total = _(this.signingStatusList).size();

            return checked * 100 / total;
        },
        approvalStatusPercent() {
            let checked = _(this.approvalStatusList).filter((status) => status.checked === true).size();
            let total = _(this.approvalStatusList).size();

            return checked * 100 / total;
        },
        approvalStatusList() {
            return [
                {
                    checked: this.draftContract.landlord_approval_requested_at !== null,
                    label: this.$t('contract-status.sent-to-landlord-for-approval.label'),
                },
                {
                    checked: this.draftContract.landlord_approved_at !== null,
                    label: this.$t('contract-status.landlord-email.label'),
                },
                {
                    checked: this.draftContract.landlord_approved_at !== null,
                    label: this.$t('contract-status.landlord-seen-contract.label'),
                },
                {
                    checked: this.draftContract.landlord_approved_at !== null,
                    label: this.$t('contract-status.landlord-has-approved.label'),
                },
            ]
        },
        signingStatusList() {
            if (this.draftContract.tenants.length > 1) {
                // Multitenant

                let totalTenants = this.draftContract.tenants.length;
                let totalTenantsSent = _(this.draftContract.tenants).filter((tenant) => tenant.pivot.is_sent).size();
                let totalTenantsOpenedEmail = _(this.draftContract.tenants).filter((tenant) => tenant.pivot.is_received).size();
                let totalTenantsSeen = _(this.draftContract.tenants).filter((tenant) => tenant.pivot.was_viewed).size();
                let totalTenantsSigned = _(this.draftContract.tenants).filter((tenant) => tenant.pivot.is_signed).size();

                return [
                    {
                        inProgress: totalTenantsSent > 0,
                        checked: totalTenantsSent === totalTenants,
                        label: this.$t('contract-status.sent-to.label') + ' ' + totalTenantsSent + ' ' + this.$t('contract-status.tenants-for-signing.label'),
                        tooltips: {
                            multiple: true,
                            items: _(this.draftContract.tenants).map(function (tenant) {
                                    return {text: tenant.email, checked: tenant.pivot.is_sent}
                                }
                            )
                        },
                    },
                    {
                        inProgress: totalTenantsOpenedEmail > 0,
                        checked: totalTenantsOpenedEmail === totalTenants,
                        label: totalTenantsOpenedEmail + ' / ' + totalTenants + ' ' + this.$t('contract-status.tenants-email.label'),
                        tooltips: {
                            multiple: true,
                            items: _(this.draftContract.tenants).map(function (tenant) {
                                    return {text: tenant.email, checked: tenant.pivot.is_received || tenant.pivot.was_viewed}
                                }
                            )
                        },
                    },
                    {
                        inProgress: totalTenantsSeen > 0,
                        checked: totalTenantsSeen === totalTenants,
                        label: totalTenantsSeen + ' / ' + totalTenants + ' ' + this.$t('contract-status.tenants-seen-contract.label'),
                        tooltips: {
                            multiple: true,
                            items: _(this.draftContract.tenants).map(function (tenant) {
                                    return {text: tenant.email, checked: tenant.pivot.was_viewed}
                                }
                            )
                        },
                    },
                    {
                        inProgress: totalTenantsSigned > 0,
                        checked: totalTenantsSigned === totalTenants,
                        label: totalTenantsSigned + ' / ' + totalTenants + ' ' + this.$t('contract-status.tenants-signed-contract.label'),
                        tooltips: {
                            multiple: true,
                            items: _(this.draftContract.tenants).map(function (tenant) {
                                    return {text: tenant.email, checked: tenant.pivot.is_signed}
                                }
                            )
                        },
                    },
                    {
                        checked: this.draftContract.is_completed,
                        label: this.$t('contract-status.landlord-signed-contract.label'),
                        tooltips: {
                            multiple: false,
                            text: moment(this.draftContract.landlord_signed_at).format('LLL')
                        }
                    },
                ]

            } else {
                // Single Tenant

                return [
                    {
                        checked: this.draftContract.tenants[0].pivot.is_sent,
                        label: this.$t('contract-status.sent-to-tenant-for-signing.label'),
                        tooltips: {
                            multiple: false,
                            text: moment(this.draftContract.tenants[0].pivot.sent_at).format('LLL')
                        }
                    },
                    {
                        checked: this.draftContract.tenants[0].pivot.is_received || this.draftContract.tenants[0].pivot.was_viewed,
                        label: this.$t('contract-status.tenant-email.label'),
                        tooltips: {
                            multiple: false,
                            text: moment(this.draftContract.tenants[0].pivot.received_at ?? this.draftContract.tenants[0].pivot.viewed_at).format('LLL')
                        }
                    },
                    {
                        checked: this.draftContract.tenants[0].pivot.was_viewed,
                        label: this.$t('contract-status.tenant-seen-contract.label'),
                        tooltips: {
                            multiple: false,
                            text: moment(this.draftContract.tenants[0].pivot.viewed_at).format('LLL')
                        }
                    },
                    {
                        checked: this.draftContract.tenants[0].pivot.is_signed,
                        label: this.$t('contract-status.tenant-signed-contract.label'),
                        tooltips: {
                            multiple: false,
                            text: moment(this.draftContract.tenants[0].pivot.signed_at).format('LLL')
                        }
                    },
                    {
                        checked: this.draftContract.is_completed,
                        label: this.$t('contract-status.landlord-signed-contract.label'),
                        tooltips: {
                            multiple: false,
                            text: moment(this.draftContract.landlord_signed_at).format('LLL')
                        }
                    },
                ]
            }

        },
    },
    components: {
        Loader,
        Header,
        Grid,
        SvgIcon,
        StatusCol,
        TableDropdown,
        ContractStatusItemList,
        ContractPreview,
        DownloadContractModal,
    },
    methods: {

        id() {
            return this.$route.params.id;
        },
        deleteContract() {
            if (confirm(this.$t('contracts.delete.confirmation'))) {
                this.$store.dispatch('deleteContract');
                this.$store.commit('setSuccessBarMessage', this.$t('digital-contract.contract-deleted.label'));
                this.$router.push('/contracts');
            }
        },
        revoke(callback) {
            if (confirm(this.$t('contract-status.confirm-revoke.label'))) {
                axios({
                    method: 'POST',
                    url: window.config.apiUrl + 'api/draft-contract/' + this.id() + '/revoke',
                    cancelToken: window.AuthorizationCancelSource.token,
                }).then(response => {
                    this.$store.commit('setSuccessBarMessage', this.$t('digital-contract.revoked.label'));
                    this.$router.push('/contracts/digital-contract/edit/' + this.id());
                });
            }
        },
        edit() {
            this.revoke(() => {
                this.$router.push('/contracts/digital-contract/edit/' + this.$route.params.id);
            });
        },
        preview() {
            this.generatingPdf = false;
            this.pdfPreviewUrl = window.config.apiUrl + 'api/contract/' + this.$route.params.id + '?token=' + this.getToken;
            this.showPdfViewver = true;
        },
        dateFormat(date) {
            return date ? moment(String(date)).format('DD-MM-YYYY') : '';
        },
        getId() {
            return this.$route.params.id;
        },
        load() {
            axios({
                method: 'GET',
                url: window.config.apiUrl + 'api/draft-contract/' + this.getId() + '/status',
                cancelToken: window.AuthorizationCancelSource.token,
            }).then(response => {
                this.loading = false;
                this.property = response.data.property;
                this.landlordName = response.data.landlordName || response.data.draftContract.landlord.email;
                this.draftContract = response.data.draftContract;
                this.readyForSignature = response.data.readyForSignature;
                this.parseProperty();
            })
        },
        parseProperty() {
            this.title = this.property.address.replace(/,.*?$/, '');
            this.subtitle = this.property.zip_code + ' ' + this.property.city;
            this.link = '/contracts';
        },

        sendEmail() {
            this.$router.push('/contracts/digital-contract/confirm/' + this.$route.params.id);
        },
        downloadModal: function () {
            this.showDownloadModal = !this.showDownloadModal;
        },
        close() {
            this.showDownloadModal = false;
        }
    },
    created() {
        this.load();
        this.$root.$on('close-contract-preview', () => {
            this.showPdfViewver = false;
        });
    }
}
</script>
